import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../../constants';
import type { OrganizationFormData, OrganizationParentAccount } from '../../../../types';
import { useOrganizationContext } from '../../../../utils/context/OrganizationContext';
import { buildParentOrganizationOptions, filterParentOrganizationOptions } from '../utils';

type ParentOrganizationFieldProps = {
  isEdit?: boolean;
};

export const ParentOrganizationField = ({ isEdit }: ParentOrganizationFieldProps) => {
  const { useOrganizationsListFetch } = useOrganizationContext();
  const { loading, organizations, error } = useOrganizationsListFetch({ variables: {}, skip: isEdit });
  const { setValue } = useFormContext<OrganizationFormData>();

  const { t } = useTranslation();

  const parentOrganizationOptions: OrganizationParentAccount[] = useMemo(
    () => buildParentOrganizationOptions(organizations),
    [organizations]
  );

  const observableSelectedParentId = useWatch<OrganizationFormData, typeof organizationFields.SELECTED_PARENT_ID>({
    name: organizationFields.SELECTED_PARENT_ID,
  });

  const handleSelectParentAccount = useCallback(
    (selectedOption: OrganizationFormData['parent_account']) => {
      const isTopLevelOrganization = selectedOption?.depth === 0;
      const feeSplittingPartner = !!selectedOption?.feature_flags?.find(({ value }) => {
        return value === Feature_Flags_Enum.FeeSplittingPartner;
      });

      setValue(organizationFields.PARENT_ACCOUNT, selectedOption);
      setValue(organizationFields.ORGANIZATION_TYPE, selectedOption?.organization_type);
      // set false for top level organizations to match initial values
      setValue(organizationFields.FEE_SPLITTING_PARTNER, isTopLevelOrganization ? false : feeSplittingPartner);
      // set false for null to match initial values
      setValue(
        organizationFields.SHOULD_DISABLE_CURATED_DEAL_FEES,
        selectedOption === null ? false : !isTopLevelOrganization
      );
    },
    [setValue]
  );

  useEffect(() => {
    if (observableSelectedParentId) {
      const parentOrganization = parentOrganizationOptions.find(option => option.id === observableSelectedParentId);
      if (parentOrganization) handleSelectParentAccount(parentOrganization);
    }
  }, [observableSelectedParentId, parentOrganizationOptions, handleSelectParentAccount]);

  if (error) throw new Error('Parent Organization fetch error');

  return (
    <RHFAutoCompleteVirtualize
      name={organizationFields.PARENT_ACCOUNT}
      options={parentOrganizationOptions || {}}
      getOptionLabel={option => option.name}
      filterOptions={filterParentOrganizationOptions}
      sx={{ mb: 2 }}
      onChange={(_, selectedOption) => handleSelectParentAccount(selectedOption)}
      textFieldProps={{
        label: t('Parent Organization'),
        placeholder: t('Select Parent Organization'),
      }}
      data-test="parent-account"
      loading={loading}
      disabled={isEdit}
      renderOptions={{
        getOptionValue: optionId => optionId.name,
        getPaddingLevel: optionId => optionId.depth ?? 1,
      }}
    />
  );
};
